<script setup lang="ts">
import { useI18n } from 'vue-i18n'

interface Props {
    /** Deals still available. */
    remaining: number
    /** Total deal capacity (`maxRedemptionsGlobal`). */
    max: number
    /**
     * i18n key for the label, interpolated with `{ remaining, max }` — lets the detail
     * (`campaign.detail.remaining`) and listing (`campaign.listing.remaining`) variants share
     * this component while keeping their own copy.
     */
    labelKey: string
}

const props = defineProps<Props>()
const { t } = useI18n()

// Starts full and depletes from right to left as availability shrinks: anchoring the filled
// element at the (default) left edge and driving its width off the *remaining* share already
// produces that effect — the right edge is what recedes as the width shrinks.
const percentage = computed(() => {
    if (props.max <= 0) return 0
    return Math.min(100, Math.max(0, Math.round((props.remaining / props.max) * 100)))
})

const label = computed(() => t(props.labelKey, { remaining: props.remaining, max: props.max }))
</script>

<template>
    <div class="flex flex-col gap-2" data-cy="campaign-availability-bar">
        <span>{{ label }}</span>

        <div
            class="h-1 w-full bg-base-300 rounded-full overflow-hidden"
            role="progressbar"
            :aria-valuenow="remaining"
            :aria-valuemin="0"
            :aria-valuemax="max"
            :aria-valuetext="label"
        >
            <div
                class="h-full bg-neutral-focus rounded-full"
                :style="{ width: `${percentage}%` }"
            />
        </div>
    </div>
</template>
